import { useEffect, useRef } from 'react';
import { 
  Building, 
  ClipboardCheck, 
  MapPin, 
  HardHat, 
  Wrench, 
  FileSearch 
} from 'lucide-react';

const services = [
  {
    id: 1,
    icon: Building,
    title: 'Construcción de Inmuebles',
    description: 'Edificación de inmuebles comerciales, institucionales, de servicios, habitacionales, naves y plantas industriales con los más altos estándares de calidad.',
  },
  {
    id: 2,
    icon: HardHat,
    title: 'Ingeniería Civil y Obra Pesada',
    description: 'Trabajos especializados en proyectos de construcción con infraestructura compleja y tecnología de punta.',
  },
  {
    id: 3,
    icon: ClipboardCheck,
    title: 'Administración de Proyectos',
    description: 'Administración y supervisión en procesos de construcción con control presupuestal y optimización de tiempos.',
  },
  {
    id: 4,
    icon: MapPin,
    title: 'Desarrollo de Terrenos',
    description: 'División de terrenos y gestión de obras de urbanización, incluyendo vialidades y redes de servicios.',
  },
  {
    id: 5,
    icon: Wrench,
    title: 'Trabajos Especializados',
    description: 'Cimentaciones, albañilería, acabados interiores y exteriores, instalaciones eléctricas y de climatización.',
  },
  {
    id: 6,
    icon: FileSearch,
    title: 'Consultoría y Diseño',
    description: 'Planteamiento, conceptualización y desarrollo de proyectos arquitectónicos y de ingeniería civil.',
  },
];

export const ServicesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="servicios"
      ref={sectionRef}
      data-testid="services-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="gold-line" />
            <span className="font-manrope uppercase tracking-widest text-xs font-bold text-[#BF9000]">
              Lo que hacemos
            </span>
            <div className="gold-line" />
          </div>
          <h2
            data-testid="services-title"
            className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-[#385723] uppercase tracking-tight mb-4"
          >
            Nuestros Servicios
          </h2>
          <p className="font-manrope text-slate-600 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Soluciones integrales de construcción con excelencia técnica y gestión eficiente
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="service-card reveal group relative overflow-hidden bg-white p-8 flex flex-col"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="icon-wrapper w-14 h-14 bg-[#385723] text-white flex items-center justify-center mb-6 transition-all duration-300">
                  <IconComponent className="w-7 h-7" />
                </div>

                {/* Content */}
                <h3 className="font-oswald text-xl font-bold text-[#385723] uppercase tracking-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-manrope text-slate-600 text-sm leading-relaxed flex-1">
                  {service.description}
                </p>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-[#92D050]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
